import { useEffect, useMemo } from 'react';
import { getChartColorWithMax } from 'components/chart/data/ChartColors';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useDataRequest } from 'hooks/useDataRequest';
import { DataResult } from 'models/data';
import { useIntl } from 'react-intl';
import { Service, useProDataService } from 'services/data.service';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { PageContainer, Tile, TileContent } from 'components/Layout/PageContainer';
import { MarketplaceShares } from 'pages/insights-pro/marketplace-tab/components/MarketplaceShares';
import { CountriesMarketplaceShares } from 'pages/insights-pro/marketplace-tab/components/CountriesMarketplaceShares';
import { CountryInfo, MarketplaceInfo } from 'pages/insights-pro/marketplace-tab/model';
import { MarketplacesReturnRates } from 'pages/insights-pro/marketplace-tab/components/MarketplacesReturnRates';
import { ReportLinkCard } from 'pages/insights-pro/common/reports/ReportLink';
import { ActiveMarketplacesPerCountry } from 'pages/insights-pro/marketplace-tab/components/ActiveMarketplacesPerCountry';
import { useModelService } from 'services/model/model.service';
import { FulfillmentMethodSharesChart } from 'pages/insights-pro/common/components/FulfillmentMethodSharesChart';
import { TopGrowthList } from 'pages/insights-pro/common/components/TopGrowthList';
import { SalesPerformanceList } from 'pages/insights-pro/common/components/SalesPerformanceList';
import { ActiveMarketplaces } from './components/ActiveMarketplaces';
import { TopPerformingMarketplaces } from './components/TopPerformingMarketplaces';
import { MarketplaceTabInfoContent } from './components/MarketplaceTabInfoContent';

const UNGROUPED_COUNT = 10;

export function MarketplaceDashboard() {
  const { fetchData } = useProDataService(Service.Marketplaces);
  const { getMeasures, getAttributes } = useModelService();

  const intl = useIntl();
  const requestData = useDataRequest();
  const { setContent } = useInfoDrawer();

  const [chnMarketplace, chnCountry] = getAttributes(['chnMarketplace', 'chnCountry']);

  const [ordGmvVal, ordQty, ordNum] = getMeasures(['ordGmvVal', 'ordQty', 'ordNum']);

  useEffect(() => {
    const marketplaceDashboardInfo = {
      titleKey: 'Marketplaces Dashboard',
      subtitleKey: 'info.content.all_dates_utc',
      content: <MarketplaceTabInfoContent />,
    };

    setContent(marketplaceDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [ordGmvVal, setContent]);

  const avgSoldItemsPerProdTitle = useMemo(
    () =>
      intl.formatMessage(
        { id: 'general.title.by-metric' },
        {
          metric: intl.formatMessage({
            id: 'model.calculated.metric.avgSoldItemsPerProduct.name',
          }),
        }
      ),
    [intl]
  );

  const extractMarketplaceInfo = (data: DataResult) => {
    const marketplaces: MarketplaceInfo[] = data.sets.marketplaces.map(entry => {
      const marketplaceName: string = chnMarketplace.getValue(entry.attributes);
      return {
        name: marketplaceName,
        isOther: false,
        color: '',
        // A marketplace is only active if it has at least one order
        isActive: ordQty.getValue(entry.values) > 0,
        entry,
      };
    });

    marketplaces.sort((a, b) => ordGmvVal.getValue(b.entry.values) - ordGmvVal.getValue(a.entry.values));

    marketplaces.forEach((_, i) => {
      marketplaces[i].color = getChartColorWithMax(i, UNGROUPED_COUNT);
      if (i >= UNGROUPED_COUNT) {
        marketplaces[i].isOther = true;
      }
    });

    return marketplaces;
  };
  const extractCountriesFromMarketplaces = (marketplaces: MarketplaceInfo[]) => {
    const countries: CountryInfo[] = [];

    marketplaces.forEach(marketplace => {
      marketplace.entry.sets.countries.forEach(countryMarketplace => {
        const countryCode = chnCountry.getRawValue(countryMarketplace.attributes);
        const country =
          countries.find(c => c.code === countryCode) ??
          (() => {
            const countryName: string = chnCountry.getValue(countryMarketplace.attributes);
            const newCountry = {
              code: countryCode,
              name: countryName,
              totalGmv: 0,
              marketplaces: [],
            };
            countries.push(newCountry);
            return newCountry;
          })();

        country.totalGmv += ordGmvVal.getValue(countryMarketplace.values);
        country.marketplaces.push({
          ...marketplace,
          entry: countryMarketplace,
        });
      });
    });
    countries.sort((a, b) => b.totalGmv - a.totalGmv);
    return countries;
  };

  const dashboardState = useDataServiceQueryResult(
    requestData,
    data => fetchData('dashboard', data),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );

      const marketplaces = extractMarketplaceInfo(response);
      const countries = extractCountriesFromMarketplaces(marketplaces);

      return {
        data: response,
        isComparisonActive,
        countryCountCompare: isComparisonActive
          ? response.sets.marketplaces.filter(country => ordQty.getValue(country.compareValues) > 0).length
          : null,
        marketplaces,
        countries,
      };
    },
    ['insights-marketplaces-dashboard']
  );

  if (!dashboardState.data && !dashboardState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const byGmvTitle = intl.formatMessage(
    {
      id: 'general.title.by-metric',
    },
    {
      metric: ordGmvVal.metric.name,
    }
  );

  if (dashboardState.isError) {
    return (
      <PageContainer>
        <Tile
          titleId="insights.marketplaces"
          errorDetails={dashboardState.error}
          testId="insights-marketplaces-dashboard-error"
        />
      </PageContainer>
    );
  }

  const dashboardData = dashboardState.data!;

  return (
    <PageContainer>
      <Tile
        titleId="insights.marketplace_tab.top_performing_header"
        additionalTitle={byGmvTitle}
        className="xl:col-span-6"
      >
        <TopPerformingMarketplaces
          marketplaces={dashboardData.marketplaces}
          isComparisonActive={dashboardData.isComparisonActive}
          gmvMeasure={ordGmvVal}
          qtyMeasure={ordQty}
        />
      </Tile>
      <Tile
        titleId="insights.marketplace_tab.marketplace_share_gmv_header"
        additionalTitle={byGmvTitle}
        className="xl:col-span-6"
      >
        <MarketplaceShares
          marketplaces={dashboardData.marketplaces}
          isComparisonActive={dashboardData.isComparisonActive}
          gmvMeasure={ordGmvVal}
        />
      </Tile>
      <ReportLinkCard
        className="xl:col-span-6"
        titleId="insights.marketplace.reports.channel_breakdown"
        descriptionId="insights.marketplace.reports.channel_breakdown.description"
        linkUrl="reports/channel-breakdown"
      />

      <ReportLinkCard
        className="xl:col-span-6"
        titleId="insights.marketplace.reports.over_time"
        descriptionId="insights.marketplace.reports.over_time.description"
        linkUrl="reports/over-time"
      />

      <Tile
        titleId="insights.marketplace_tab.marketplace_top_growing_header"
        additionalTitle={intl.formatMessage({ id: 'insights.measure.orderedGMVGrowth.by' })}
        className="xl:col-span-6"
      >
        <TopGrowthList
          items={dashboardData.marketplaces}
          isComparisonActive={dashboardData.isComparisonActive}
          gmvMeasure={ordGmvVal}
          getNameCellProps={item => ({
            name: item.name,
            color: item.color,
          })}
        />
      </Tile>

      <Tile
        titleId="insights.marketplace_tab.marketplace_return_rates_header"
        additionalTitle={intl.formatMessage({
          id: 'insights.marketplace_tab.marketplace_return_rates_header_subheader',
        })}
        className="xl:col-span-6"
      >
        <MarketplacesReturnRates
          total={dashboardData.data}
          marketplaces={dashboardData.marketplaces}
          isComparisonActive={dashboardData.isComparisonActive}
        />
      </Tile>
      <ReportLinkCard
        titleId="insights.marketplace.reports.net_sales_rates"
        descriptionId="insights.marketplace.reports.net_sales_rates.description"
        linkUrl="reports/net-sales-rates"
      />
      <Tile
        titleId="insights.marketplace_tab.marketplace_share_country_header"
        additionalTitle={intl.formatMessage({
          id: 'insights.marketplace_tab.marketplace_share_country_header_subheader',
        })}
        className="xl:col-span-6"
      >
        <CountriesMarketplaceShares
          marketplaces={dashboardData.marketplaces}
          countries={dashboardData.countries}
          gmvMeasure={ordGmvVal}
        />
      </Tile>
      <Tile
        titleId="insights.products.products_sold_by_marketplaces_header"
        additionalTitle={avgSoldItemsPerProdTitle}
        className="xl:col-span-6"
      >
        <TileContent className="px-0">
          <SalesPerformanceList
            attribute={chnMarketplace}
            isComparisonActive={dashboardData.isComparisonActive}
            entries={dashboardData.marketplaces.map(m => m.entry)}
          />
        </TileContent>
      </Tile>
      <Tile
        titleId="insights.marketplace_tab.active_marketplaces_header"
        additionalTitle={intl.formatMessage(
          {
            id: 'general.title.by-metric',
          },
          {
            metric: ordNum.metric.name,
          }
        )}
        className="xl:col-span-6"
      >
        <ActiveMarketplaces
          marketplaces={dashboardData.marketplaces}
          isComparisonActive={dashboardData.isComparisonActive}
          qtyMeasure={ordQty}
        />
      </Tile>

      <Tile
        titleId="fulfillment.method.fulfillment_header"
        additionalTitle={byGmvTitle}
        className="xl:col-span-6 row-span-2"
      >
        <FulfillmentMethodSharesChart
          data={dashboardData.marketplaces}
          name={m => m.name}
          fulfillmentMethods={m => m.entry.sets?.fulfillmentMethods || []}
          gmvMeasure={ordGmvVal}
        />
      </Tile>
      <Tile
        titleId="insights.marketplace_tab.active_marketplaces_header"
        additionalTitle={intl.formatMessage({
          id: 'insights.marketplace_tab.active_marketplaces_header_subheader',
          defaultMessage: 'per Country',
        })}
        className="xl:col-span-6"
      >
        <ActiveMarketplacesPerCountry countries={dashboardData.countries} gmvMeasure={ordGmvVal} qtyMeasure={ordQty} />
      </Tile>
    </PageContainer>
  );
}
